import { useState } from 'react'
import { useNavigation } from '@react-navigation/native'
import { useQueryClient } from '@tanstack/react-query'
import { GenericNavigationProp } from '@navigation'
import { useShareMyLocation, useSignIn } from '@hooks'
import { useAuthCredentialsStore, useCurrentUserStore } from '@store'
import { isUncodedBadRequest, isValidEmail, normalizeEmail, translateApiError } from '@utils'
import { useLanguage } from '@context'

interface useLoginResult {
  email: string
  password: string
  isPending: boolean
  error: string
  setEmail: React.Dispatch<React.SetStateAction<string>>
  setPassword: React.Dispatch<React.SetStateAction<string>>
  handleSignIn: () => void
}

export const useLogin = (): useLoginResult => {
  const navigation = useNavigation<GenericNavigationProp>()
  const { t } = useLanguage()
  const queryClient = useQueryClient()
  const { mutate: signInMutation, isPending } = useSignIn()
  const shareMyLocation = useShareMyLocation()
  const { setAuthCredentials } = useAuthCredentialsStore()
  const { setUserID } = useCurrentUserStore()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')

  const handleSignIn = () => {
    setError('')

    // Trailing space / capitalisation from the keyboard breaks the API's @IsEmail
    // (→ 400). Password is intentionally NOT normalised.
    const cleanedEmail = normalizeEmail(email)

    if (!cleanedEmail || !password) {
      setError(t('login.error.noInfo'))
      return
    }

    // The API answers a malformed address with a bare 400: say what is wrong instead.
    if (!isValidEmail(cleanedEmail)) {
      setError(t('login.error.invalidEmail'))
      return
    }

    signInMutation(
      { email: cleanedEmail, password },
      {
        onSuccess: async (user) => {
          setAuthCredentials(cleanedEmail)
          // The token is now set, so authed queries become enabled. Refresh the
          // Premium status for the account that just signed in (logout clears
          // the whole cache, so this is the login half of the auth lifecycle).
          void queryClient.invalidateQueries({ queryKey: ['subscription'] })
          if (!user.account.isOnboardingCompleted) {
            (navigation as any).navigate('Auth', { screen: 'RegistrationSteps' })
          } else {
            setUserID(user.account.id)
            // My position for my contacts, at most once a day.
            void shareMyLocation(user.account.id)
          }
        },
        onError: (err) => {
          // The only field the API validates here is the e-mail: same sentence
          // as the check above.
          if (isUncodedBadRequest(err)) {
            setError(t('login.error.invalidEmail'))
            return
          }
          const { message } = translateApiError(t, err)
          setError(message)
        },
      },
    )
  }

  return {
    email,
    password,
    isPending,
    error,
    setEmail,
    setPassword,
    handleSignIn,
  }
}
